import {NlaUser} from "@/lib/models.ts";
import { logger, LogCode } from "@/logger";
import {
  AllAttemptsFailedError,
  UnknownError,
  InternalServerError,
  BadGatewayError,
  ServiceUnavailableError,
  GatewayTimeoutError,
  FTSpecialAccessApiError,
  UnauthorisedError,
  ForbiddenError,
  ContentApiJsonError,
  NotFoundError,
  ResponseError
} from '@/errors';
import {ContentApiErrorResponse} from "@/types.ts";

const isFTSpecialAccessApiErrorResponse = (
  error: unknown
): error is ContentApiErrorResponse =>
(error as ContentApiErrorResponse)?.error?.code == "UserNotLicencedForFTAccess";

const isContentApiErrorResponse = (
  error: unknown
): error is ContentApiErrorResponse =>
  (error as ContentApiErrorResponse)?.error?.code !== undefined;

const Fetch = window.fetch;
type Fetch = typeof Fetch;
type FetchArgs = Parameters<Fetch>;
/**
 * `Fetcher` is essentially just a wrapper around a local const `Fetch`, by default pointing to `window.fetch`
 * and can be used in the exact same way and returns the expected `Promise<Response>`.
 */
export type Fetcher = (...args: FetchArgs) => ReturnType<Fetch>;
export const Fetcher: Fetcher = async (...args) => {
  try {
    const res = await Fetch(...args);
    if (!res.ok) {
      // These are server errors, in these cases the server we can't trust the server has been able to respond in JSON so we should just throw a generic error for them
      if (res.status === 500) {
        return Promise.reject(new InternalServerError());
      }
      if (res.status === 502) {
        return Promise.reject(new BadGatewayError());
      }
      if (res.status === 503) {
        return Promise.reject(new ServiceUnavailableError());
      }
      if (res.status === 504) {
        return Promise.reject(new GatewayTimeoutError());
      }
      // Try to parse the server's response as a JSON object so the server can provide additional context
      try {
        const json: ContentApiErrorResponse = await res.json();
        if (isFTSpecialAccessApiErrorResponse(json)) {
          return Promise.reject(new FTSpecialAccessApiError(res.status, json));
        }
        if (isContentApiErrorResponse(json)) {
          return Promise.reject(new ContentApiJsonError(res.status, json));
        }
      } catch (e) {
        logger.error('Error when parsing JSON response from server', {}, e);
      }
      // If JSON parsing fails, we should handle the other status codes before falling back to a generic error.
      if (res.status === 401) {
        return Promise.reject(new UnauthorisedError());
      }
      if (res.status === 403) {
        return Promise.reject(new ForbiddenError());
      }
      if (res.status === 404) {
        return Promise.reject(new NotFoundError());
      }
      return Promise.reject(new ResponseError(res.statusText));
    }
    return res;
  } catch (e) {
    if (e instanceof Error) {
      return Promise.reject(new UnknownError(e.message));
    }
    return Promise.reject(new UnknownError());
  }
};

export async function fetchFirstSuccess(nlaUsers: NlaUser[], url: string, tokenString: string, correlationId: string) {
  const errors = [];
  const nlaUserIds = nlaUsers.map(user => user.id) ?? [];
  for (const nlaUser of nlaUsers) {
    try {
      return await Fetcher(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenString}`,
          'Multi-User-User-ID': nlaUser.id,
          'Correlation-Id': correlationId,
        }
      })
    } catch (e) {
      errors.push(e);
      logger.warn(
        `Unsuccessful request as user`,
        {
          url,
          component: 'lib/fetcher.ts',
          pathname: window.location.pathname,
          route: window.location.pathname + window.location.search,
          correlationId,
          logCode: LogCode.FAILED_TO_FETCH,
          dataFields: {
            userIds: nlaUserIds,
            userId: nlaUser.id,
          },
        },
        e
      );
    }
  }
  throw new AllAttemptsFailedError(url, errors)
}
